import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SortHead, sortRows } from "@/components/SortHead";

const SORTERS = {
  number: (x) => x.quote_number, signed: (x) => x.signed_at || "", seller: (x) => x.reseller_name || "",
  owner: (x) => (x.sector_owners || []).join(", "), client: (x) => [x.client?.name, x.client?.firstname].filter(Boolean).join(" "),
};
import { Download, Loader2, ClipboardList, FileText, MessageSquarePlus } from "lucide-react";
import { OrderClientDialog, AdvComments } from "@/components/OrderClientDialog";
import { OrderTracking } from "@/components/OrderTracking";
import { OrderRefs } from "@/components/OrderRefs";
import { exportOrdersCsv } from "@/lib/exportOrders";
import { toast } from "sonner";
import { PosterPreview } from "@/components/PosterPreview";
import { generateQuotePdf } from "@/lib/pdfLazy";
import { MultiFilter, inSel } from "@/components/MultiFilter";
import { ReplaceBadge } from "@/components/ReplaceBadge";

const FIN_L = { ok: "Financement OK", study: "Financement en étude", none: "Pas de financement" };
const has = (v) => (v ? "Renseigné" : "Manquant");
const corinneSent = (x) => !!x.corinne_sent || (x.send_log || []).some((l) => (l.to || []).includes("corinne.golfier@fautras.com"));
const COL_FILTERS = [
  ["model", "Modèle", (x) => [x.model_name || "—"]],
  ["month", "Mois de signature", (x) => [x.signed_at ? x.signed_at.slice(0, 7) : "—"]],
  ["seller", "Vendeur", (x) => [x.reseller_name || "—"]],
  ["owner", "Responsable du secteur", (x) => (x.sector_owners || []).length ? x.sector_owners : ["Aucun"]],
  ["sector", "Secteur", (x) => [x.stock_order ? "Stock / démo" : x.out_of_sector ? "Commande hors secteur" : "Commande secteur"]],
  ["client", "Client", (x) => [[x.client?.name, x.client?.firstname].filter(Boolean).join(" ") || "—"]],
  ["deposit", "Acompte", (x) => [x.stock_order ? "Sans objet" : x.deposit_received ? "Acompte reçu" : "Acompte non reçu"]],
  ["financing", "Financement", (x) => [x.stock_order ? "Sans objet" : FIN_L[x.financing_choice] || "À choisir"]],
  ["code", "Code client Divalto", (x) => [has((x.client_divalto_code || "").trim())]],
  ["order", "Numéro cde Divalto", (x) => [has((x.divalto_order || "").trim())]],
  ["ref", "Réf cde revendeur", (x) => [has((x.reseller_ref || "").trim())]],
  ["adv", "ADV", (x) => [isProcessed(x) ? "Traitée par l'ADV" : "À traiter"]],
  ["corinne", "Transmis à Corinne", (x) => [corinneSent(x) ? "Transmis à Corinne" : x.out_of_sector ? "Hors secteur (non transmis)" : x.stock_order === "stock" ? "Stock (non transmis)" : "Pas encore transmis"]],
];
const monthLabel = (v) => (/^\d{4}-\d{2}$/.test(v) ? `${v.slice(5)}/${v.slice(0, 4)}` : v);

const ColumnFilters = ({ quotes, filters, setFilters }) => {
  const { t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="adv-column-filters">
      {COL_FILTERS.map(([k, label, fn]) => {
        const vals = [...new Set(quotes.flatMap(fn))].sort((a, b) => (k === "month" ? b.localeCompare(a) : a.localeCompare(b, "fr", { sensitivity: "base" })));
        const options = vals.map((v) => ({ value: v, label: k === "month" ? monthLabel(v) : t(v) }));
        return <MultiFilter key={k} label={t(label)} options={options} sel={filters[k] ?? null} className="w-auto max-w-[16rem] h-8 text-xs"
          onChange={(s) => setFilters((f) => ({ ...f, [k]: s }))} testid={`adv-filter-${k}`} />;
      })}
      {Object.values(filters).some((s) => s != null) && <Button size="sm" variant="ghost" className="text-xs underline" onClick={() => setFilters({})} data-testid="adv-filters-reset">{t("Réinitialiser les filtres")}</Button>}
    </div>
  );
};

const VIEWS = [["todo", "À traiter"], ["nodivalto", "Sans numéro cde Divalto"], ["oos", "Hors secteur"], ["done", "Traitées par l'ADV"], ["all", "Toutes les commandes"]];
export const isProcessed = (x) => !!(x.client_divalto_code || "").trim() && !!(x.divalto_order || "").trim();
const inView = (x, k) => k === "all" || (k === "todo" ? !isProcessed(x) : k === "done" ? isProcessed(x) : k === "nodivalto" ? !x.divalto_order : !!x.out_of_sector);

const AdvStatus = ({ quote }) => {
  const { t } = useI18n();
  return isProcessed(quote)
    ? <Badge variant="outline" className="text-[10px] border-emerald-600 text-emerald-700 whitespace-nowrap" data-testid={`adv-status-${quote.quote_number}`}>{t("Traitée par l'ADV")}</Badge>
    : <Badge variant="outline" className="text-[10px] border-amber-600 text-amber-700 whitespace-nowrap" data-testid={`adv-status-${quote.quote_number}`}>{t("À traiter")}</Badge>;
};

export default function AdvOrders() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [quotes, setQuotes] = useState(null);
  const [view, setView] = useState("todo");
  const [q, setQ] = useState("");
  const [pdf, setPdf] = useState(null);
  const [opening, setOpening] = useState("");
  const openPdf = async (x) => {
    setOpening(x.id);
    try {
      let data;
      if (x.pdf_path) {
        data = (await api.get(`/files/${x.pdf_path}`, { responseType: "arraybuffer" })).data;
      } else {
        const { data: full } = await api.get(`/quotes/${x.id}`);
        data = await (await generateQuotePdf(full, { blob: true })).arrayBuffer();
      }
      const save = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([data], { type: "application/pdf" })); a.download = `Commande_${x.quote_number}.pdf`; a.click(); };
      setPdf({ data, save, number: x.quote_number });
    } catch { toast.error(t("PDF de la commande signée introuvable")); } finally { setOpening(""); }
  };
  useEffect(() => {
    const load = () => api.get("/quotes").then(({ data }) => setQuotes(data.filter((x) => !!x.signature && !x.cancelled)))
      .catch(() => { toast.error(t("Impossible de charger les commandes")); setQuotes([]); });
    load();
    window.addEventListener("net:back", load);
    return () => { window.removeEventListener("net:back", load); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [sort, setSort] = useState({ key: "signed", dir: -1 });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filters, setFilters] = useState({});
  const list = useMemo(() => sortRows((quotes || []).filter((x) => {
    const okCols = COL_FILTERS.every(([k, , fn]) => filters[k] == null || fn(x).some((v) => inSel(filters[k], v)));
    const okView = inView(x, view);
    const term = q.trim().toLowerCase();
    const hay = [x.quote_number, x.reseller_name, x.client?.name, x.client?.firstname, x.model_name, x.divalto_order, x.reseller_ref, x.client_divalto_code].join(" ").toLowerCase();
    const day = (x.signed_at || "").slice(0, 10);
    const okDate = (!dateFrom || day >= dateFrom) && (!dateTo || day <= dateTo);
    return okView && okCols && okDate && (!term || hay.includes(term));
  }), sort, SORTERS), [quotes, view, q, sort, dateFrom, dateTo, filters]);
  const patch = (d) => setQuotes((qs) => qs.map((x) => (x.id === d.id ? { ...x, ...d } : x)));
  const [ocTarget, setOcTarget] = useState(null);
  const patchClient = (cid, code) => setQuotes((qs) => qs.map((x) => (x.client_id === cid ? { ...x, client_divalto_code: code } : x)));

  if (!(user?.role === "admin" || user?.adv)) return <div className="p-10 text-muted-foreground">{t("Accès réservé à l'ADV")}</div>;
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="adv-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <ClipboardList className="w-7 h-7 text-primary" />
        <h1 className="text-3xl font-semibold">{t("Commandes à traiter")}</h1>
        <Button variant="outline" className="ml-auto gap-2" disabled={!list.length} onClick={() => exportOrdersCsv(list, "commandes-adv")} data-testid="adv-export">
          <Download className="w-4 h-4" /> {t("Exporter (Excel)")}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {VIEWS.map(([k, l]) => (
          <Button key={k} size="sm" variant={view === k ? "default" : "outline"} className="rounded-full" onClick={() => setView(k)} data-testid={`adv-view-${k}`}>
            {t(l)} ({(quotes || []).filter((x) => inView(x, k)).length})
          </Button>
        ))}
        <div className="flex items-center gap-1.5 ml-auto" data-testid="adv-date-filter">
          <span className="text-xs text-muted-foreground">{t("Signée du")}</span>
          <Input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="w-[9.5rem]" data-testid="adv-date-from" />
          <span className="text-xs text-muted-foreground">{t("au")}</span>
          <Input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="w-[9.5rem]" data-testid="adv-date-to" />
          {(dateFrom || dateTo) && <Button size="sm" variant="ghost" className="text-xs underline px-2" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="adv-date-clear">{t("Effacer")}</Button>}
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Rechercher…")} className="w-56" data-testid="adv-search" />
      </div>
      {quotes && <ColumnFilters quotes={quotes} filters={filters} setFilters={setFilters} />}
      {!quotes ? <Loader2 className="w-6 h-6 animate-spin" /> : !list.length ? (
        <p className="text-muted-foreground py-10" data-testid="adv-empty">{t("Aucune commande à afficher")}</p>
      ) : (
        <div className="rounded-xl border border-border overflow-auto max-h-[calc(100vh-230px)]" data-testid="adv-table-scroll">
          <table className="w-full caption-bottom text-sm">
            <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_hsl(var(--border))]"><TableRow>
              <SortHead k="number" sort={sort} setSort={setSort} testid="adv-sort-number">{t("Commande")}</SortHead>
              <SortHead k="signed" sort={sort} setSort={setSort} testid="adv-sort-signed">{t("Signée le")}</SortHead>
              <SortHead k="seller" sort={sort} setSort={setSort} testid="adv-sort-seller">{t("Vendeur")}</SortHead>
              <SortHead k="owner" sort={sort} setSort={setSort} testid="adv-sort-owner">{t("Responsable du secteur")}</SortHead>
              <SortHead k="client" sort={sort} setSort={setSort} testid="adv-sort-client">{t("Client")}</SortHead>
              <TableHead>{t("Acompte et financement")}</TableHead><TableHead>{t("Références Divalto")}</TableHead><TableHead>{t("ADV")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((x) => (
                <TableRow key={x.id} data-testid={`adv-row-${x.quote_number}`} className="align-top">
                  <TableCell>
                    <button type="button" onClick={() => openPdf(x)} disabled={!!opening} title={t("Voir le PDF de la commande")}
                      className="font-mono text-sm text-primary underline underline-offset-2 inline-flex items-center gap-1 whitespace-nowrap" data-testid={`adv-open-pdf-${x.quote_number}`}>
                      {opening === x.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}{x.quote_number}
                    </button>
                    <div className="text-xs text-muted-foreground">{x.model_name}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {x.stock_order && <Badge variant="outline" className="text-[10px]">{x.stock_order === "demo" ? t("Démo") : t("Stock")}</Badge>}
                      {x.paper_order && <Badge variant="outline" className="text-[10px] border-[#9E7B41] text-[#9E7B41]">{t("Bon papier")}</Badge>}
                    </div>
                    <ReplaceBadge quote={x} />
                  </TableCell>
                  <TableCell className="text-sm whitespace-nowrap" data-testid={`adv-signed-${x.quote_number}`}>{x.signed_at ? new Date(x.signed_at).toLocaleDateString("fr-FR") : "—"}</TableCell>
                  <TableCell className="text-sm">{x.reseller_name}
                    {x.entered_by_name && <div className="text-[10px] text-muted-foreground" data-testid={`adv-entered-by-${x.quote_number}`}>{t("Saisi par")} {x.entered_by_name}</div>}</TableCell>
                  <TableCell className="text-sm" data-testid={`adv-sector-owner-${x.quote_number}`}>
                    {x.stock_order ? "—" : (x.sector_owners || []).length ? x.sector_owners.map((o) => <div key={o}>{o}</div>) : <span className="text-muted-foreground">{t("Aucun")}</span>}
                    {!x.stock_order && (x.out_of_sector
                      ? <Badge variant="outline" className="mt-1 text-[10px] border-amber-600 text-amber-700" data-testid={`adv-sector-tag-${x.quote_number}`}>{t("Commande hors secteur")}</Badge>
                      : <Badge variant="outline" className="mt-1 text-[10px] border-emerald-600 text-emerald-700" data-testid={`adv-sector-tag-${x.quote_number}`}>{t("Commande secteur")}</Badge>)}
                  </TableCell>
                  <TableCell className="text-sm">{[x.client?.name, x.client?.firstname].filter(Boolean).join(" ") || "—"}
                    <div className="text-xs text-muted-foreground">{x.client?.postal} {x.client?.city}</div>
                    {(x.adv_comments || []).length > 0 && <div className="mt-1.5 max-w-[260px]"><AdvComments list={x.adv_comments} n={x.quote_number} /></div>}
                    <Button size="sm" variant="ghost" className="h-6 px-1.5 mt-1 text-[11px] gap-1" onClick={() => setOcTarget(x)} data-testid={`adv-client-edit-${x.quote_number}`}>
                      <MessageSquarePlus className="w-3.5 h-3.5" />{t("Client / précisions")}
                    </Button></TableCell>
                  <TableCell>{x.stock_order ? <span className="text-xs text-muted-foreground">—</span>
                    : <OrderTracking quote={x} editable onChange={patch} />}</TableCell>
                  <TableCell><OrderRefs quote={x} canDivalto canRef onChange={patch} onClientCode={patchClient} /></TableCell>
                  <TableCell><AdvStatus quote={x} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </table>
        </div>
      )}
      <OrderClientDialog quote={ocTarget} onClose={() => setOcTarget(null)} onSaved={(id, p) => { setOcTarget(null); patch({ id, ...p }); }} />
      <PosterPreview pdf={pdf} onClose={() => setPdf(null)} testid="adv-order-preview" printable title={`${t("Commande signée")} ${pdf?.number || ""}`} description="" />
    </div>
  );
}
